import React from 'react';
import { motion } from 'framer-motion';

/**
 * Static SVG agent-network diagram for the "How it works" section.
 * Shows the orchestrator in the middle, agents around it, with animated dashed lines.
 */
const NODES = [
  { id: 'sdr',       label: 'SDR',        x: 50,  y: 60,  color: '#FF6B1A' },
  { id: 'mkt',       label: 'Marketing',  x: 250, y: 30,  color: '#39FF14' },
  { id: 'support',   label: 'Support',    x: 460, y: 60,  color: '#FFB17A' },
  { id: 'research',  label: 'Research',   x: 80,  y: 230, color: '#39FF14' },
  { id: 'ops',       label: 'Ops',        x: 430, y: 230, color: '#39FF14' },
  { id: 'finance',   label: 'Finance',    x: 60,  y: 380, color: '#FF6B1A' },
  { id: 'data',      label: 'Data',       x: 250, y: 410, color: '#FFB17A' },
  { id: 'hr',        label: 'HR',         x: 460, y: 380, color: '#FF6B1A' },
];
const CX = 255;
const CY = 220;

export const AgentNetworkDiagram = ({ className = '' }) => {
  return (
    <svg viewBox="0 0 510 440" className={`w-full h-full ${className}`} aria-hidden>
      <defs>
        <radialGradient id="orchGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFD3A8" />
          <stop offset="55%" stopColor="#FF6B1A" />
          <stop offset="100%" stopColor="#3a1a08" />
        </radialGradient>
        <linearGradient id="lineGrad" x1="0" x2="1">
          <stop offset="0%" stopColor="#FF6B1A" stopOpacity="0.0" />
          <stop offset="50%" stopColor="#FF6B1A" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#39FF14" stopOpacity="0.0" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Background subtle hex grid */}
      <g opacity="0.06" stroke="#FFB17A" strokeWidth="0.5" fill="none">
        {Array.from({ length: 8 }).map((_, i) => (
          <circle key={`hexring-${i}`} cx={CX} cy={CY} r={50 + i * 30} />
        ))}
      </g>

      {/* Connection lines + flowing dashes */}
      {NODES.map((n) => {
        const dx = CX - n.x;
        const dy = CY - n.y;
        return (
          <g key={`line-${n.id}`}>
            <line
              x1={n.x} y1={n.y} x2={CX} y2={CY}
              stroke={n.color} strokeOpacity="0.25" strokeWidth="1"
            />
            {/* Animated dashed packet flowing inward */}
            <motion.line
              x1={n.x} y1={n.y} x2={CX} y2={CY}
              stroke={n.color} strokeWidth="2"
              strokeDasharray="2 28"
              initial={{ strokeDashoffset: 30 }}
              animate={{ strokeDashoffset: -200 }}
              transition={{ duration: 4 + (Math.abs(dx) + Math.abs(dy)) / 200, repeat: Infinity, ease: 'linear' }}
              opacity="0.85"
              filter="url(#glow)"
            />
          </g>
        );
      })}

      {/* Orchestrator */}
      <motion.g
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
        style={{ transformOrigin: `${CX}px ${CY}px` }}
      >
        <polygon
          points={`${CX},${CY - 36} ${CX + 31},${CY - 18} ${CX + 31},${CY + 18} ${CX},${CY + 36} ${CX - 31},${CY + 18} ${CX - 31},${CY - 18}`}
          fill="none" stroke="#39FF14" strokeOpacity="0.55"
        />
      </motion.g>
      <motion.g
        animate={{ rotate: [0, -360] }}
        transition={{ duration: 80, repeat: Infinity, ease: 'linear' }}
        style={{ transformOrigin: `${CX}px ${CY}px` }}
      >
        <polygon
          points={`${CX},${CY - 50} ${CX + 43},${CY - 25} ${CX + 43},${CY + 25} ${CX},${CY + 50} ${CX - 43},${CY + 25} ${CX - 43},${CY - 25}`}
          fill="none" stroke="#FF6B1A" strokeOpacity="0.45"
        />
      </motion.g>
      <circle cx={CX} cy={CY} r="28" fill="url(#orchGrad)" filter="url(#glow)" />
      <text x={CX} y={CY + 4} textAnchor="middle" fontSize="10" fontWeight="600" fill="#FFFFFF" fontFamily="'Space Grotesk', sans-serif">CORE</text>

      {/* Expanding pulse */}
      <motion.circle
        cx={CX} cy={CY} r={30}
        fill="none" stroke="#FF6B1A" strokeWidth="1"
        initial={{ r: 30, opacity: 0.6 }}
        animate={{ r: [30, 110], opacity: [0.6, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeOut' }}
      />
      <motion.circle
        cx={CX} cy={CY} r={30}
        fill="none" stroke="#39FF14" strokeWidth="1"
        initial={{ r: 30, opacity: 0.55 }}
        animate={{ r: [30, 90], opacity: [0.55, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeOut', delay: 1.2 }}
      />

      {/* Agent nodes */}
      {NODES.map((n, i) => (
        <g key={n.id}>
          {/* node halo */}
          <motion.circle
            cx={n.x} cy={n.y} r={18}
            fill="none" stroke={n.color} strokeOpacity="0.35"
            initial={{ r: 18 }}
            animate={{ r: [18, 22, 18] }}
            transition={{ duration: 2.4 + (i * 0.15), repeat: Infinity, ease: 'easeInOut' }}
          />
          <circle cx={n.x} cy={n.y} r="11" fill={n.color} fillOpacity="0.18" stroke={n.color} strokeWidth="1.5" />
          <circle cx={n.x} cy={n.y} r="4" fill={n.color} />
          <text x={n.x} y={n.y - 26} textAnchor="middle" fontSize="11" fill="#E6ECF7" fontFamily="'IBM Plex Mono', monospace" opacity="0.85">
            {n.label}
          </text>
        </g>
      ))}
    </svg>
  );
};
